import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/utils/apiClient";
import downloadFile from "@/utils/fileDownloader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Calendar, Upload, Check, X, Clock, Download } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const getPhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const backendBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  return `${backendBase}${url.startsWith('/') ? '' : '/'}${url}`;
};

const AttendanceManagement = ({ permissions = {} }) => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const today = new Date().toISOString().split('T')[0];
  const [attendance, setAttendance] = useState({});
  const [existingAttendance, setExistingAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [stats, setStats] = useState(null);
  const [bulkStartDate, setBulkStartDate] = useState('');
  const [bulkEndDate, setBulkEndDate] = useState('');
  
  const canUpdate = permissions?.can_update_attendance || false;

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    if (selectedDate && students.length > 0) {
      fetchExistingAttendance();
    }
  }, [selectedDate, students]);

  const fetchStudents = async () => {
    try {
      const response = await apiClient.get('/v1/warden/students/my-students');
      setStudents(response.data.students || []);
    } catch (error) {
      toast.error("Failed to fetch students");
      console.error(error);
    }
  };

  const fetchExistingAttendance = async () => {
    try {
      const response = await apiClient.get('/v1/warden/attendance/date', {
        params: { date: selectedDate }
      });
      
      const existing = {};
      response.data.forEach(record => {
        existing[record.student_id] = record.status;
      });
      setExistingAttendance(existing);
      
      // Calculate stats
      const present = Object.values(existing).filter(s => s === 'present').length;
      const absent = Object.values(existing).filter(s => s === 'absent').length;
      const leave = Object.values(existing).filter(s => s === 'leave').length;
      const unmarked = students.length - present - absent - leave;
      
      setStats({ present, absent, leave, unmarked, total: students.length });
    } catch (error) {
      setExistingAttendance({});
      setStats({ present: 0, absent: 0, leave: 0, unmarked: students.length, total: students.length });
    }
  };

  const handleAttendanceChange = (studentId, status) => {
    setAttendance(prev => ({ ...prev, [studentId]: status }));
  };

  const handleMarkAll = (status) => {
    const newAttendance = {};
    students.forEach(student => {
      if (!existingAttendance[student.id]) {
        newAttendance[student.id] = status;
      }
    });
    setAttendance(newAttendance);
  };

  const handleSubmit = async () => {
    if (Object.keys(attendance).length === 0) {
      toast.error("Please mark attendance for at least one student");
      return;
    }

    setLoading(true);
    const count = Object.keys(attendance).length;
    try {
      const records = Object.entries(attendance).map(([studentId, status]) => ({
        student_id: studentId,
        status: status
      }));

      await apiClient.post('/v1/warden/attendance/batch-mark', {
        date: selectedDate,
        records: records
      });

      toast.success(`Attendance marked for ${count} student(s)`);
      setAttendance({});
      fetchExistingAttendance();
    } catch (error) {
      // Fallback to individual mark in case batch fails
      try {
        const promises = Object.entries(attendance).map(([studentId, status]) =>
          apiClient.post('/v1/warden/attendance/mark', {
            student_id: studentId,
            date: selectedDate,
            status: status
          })
        );
        
        await Promise.all(promises);
        toast.success(`Attendance marked for ${count} student(s)`);
        setAttendance({});
        fetchExistingAttendance();
      } catch (fallbackError) {
        toast.error(error.response?.data?.detail || "Failed to mark attendance");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDownload = async () => {
    if (!bulkStartDate || !bulkEndDate) {
      toast.error("Please select start and end dates");
      return;
    }
    
    try {
      const response = await apiClient.get('/v1/warden/attendance/template', {
        params: {
          start_date: bulkStartDate,
          end_date: bulkEndDate
        },
        responseType: 'blob'
      });
      
      const fileName = `attendance_template_${bulkStartDate}_to_${bulkEndDate}.xlsx`;
      const mimeType = response.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      await downloadFile(response.data, fileName, mimeType);
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      toast.error("Please select a file");
      return;
    }

    const formData = new FormData();
    formData.append('file', bulkFile);

    setLoading(true);
    try {
      const response = await apiClient.post('/v1/warden/attendance/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Attendance uploaded: ${response.data.successful} successful, ${response.data.failed} failed`);
      setBulkFile(null);
      fetchExistingAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload attendance");
    } finally {
      setLoading(false);
    }
  };

  const getStatusForStudent = (studentId) => {
    return attendance[studentId] || existingAttendance[studentId] || null;
  };

  if (!canUpdate) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-8">
              <Clock className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Permission Required</h3>
              <p className="text-gray-600">You don't have permission to manage attendance.</p>
              <p className="text-sm text-gray-500 mt-2">Contact your admin to enable attendance management.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold" style={{ color: "#2C5F7F" }}>Attendance Management</h1>
        {stats && (
          <div className="flex gap-4 text-sm">
            <span className="text-green-600 font-medium">✓ {stats.present}</span>
            <span className="text-red-600 font-medium">✗ {stats.absent}</span>
            <span className="text-blue-600 font-medium">⊙ {stats.leave}</span>
            <span className="text-gray-600">⊘ {stats.unmarked}</span>
          </div>
        )}
      </div>

      <Tabs defaultValue="individual">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="individual">Individual Marking</TabsTrigger>
          <TabsTrigger value="bulk">Bulk Upload</TabsTrigger>
        </TabsList>

        <TabsContent value="individual">
          <Card>
            <CardHeader>
              <CardTitle className="flex justify-between items-center">
                <div>
                  <input
                    type="date"
                    max={today}
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="px-3 py-2 border rounded-lg"
                  />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleMarkAll('present')}>
                    Mark All Present
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {students.map((student) => {
                  const currentStatus = getStatusForStudent(student.id);
                  const isMarked = existingAttendance[student.id];
                  
                  return (
                    <div key={student.id} className={`flex items-center justify-between p-3 border rounded-lg ${
                      isMarked ? 'bg-gray-50' : 'bg-white'
                    }`}>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10 border border-blue-100">
                          <AvatarImage src={getPhotoUrl(student.photo_url)} alt={student.name} className="object-cover" />
                          <AvatarFallback className="bg-blue-100 text-sm font-semibold" style={{ color: "#7CB9E8" }}>
                            {student.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{student.name}</p>
                          <p className="text-sm text-gray-600">{student.class_name} - Room {student.room_number || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {['present', 'absent', 'half-day', 'leave', 'holiday'].map((status) => (
                          <Button
                            key={status}
                            size="sm"
                            variant={currentStatus === status ? "default" : "outline"}
                            onClick={() => handleAttendanceChange(student.id, status)}
                            disabled={isMarked}
                            className={currentStatus === status ? (status === 'present' ? 'bg-green-500' : status === 'absent' ? 'bg-red-500' : 'bg-blue-500') : ''}
                          >
                            {status === 'present' && <Check className="w-4 h-4 mr-1" />}
                            {status === 'absent' && <X className="w-4 h-4 mr-1" />}
                            {(status === 'leave' || status === 'half-day' || status === 'holiday') && <Clock className="w-4 h-4 mr-1" />}
                            {status === 'half-day' ? 'Half-day' : status.charAt(0).toUpperCase() + status.slice(1)}
                          </Button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {Object.keys(attendance).length > 0 && (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full mt-4 btn-primary text-white"
                >
                  {loading ? "Saving..." : `Submit Attendance for ${Object.keys(attendance).length} Student(s)`}
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bulk">
          <Card>
            <CardHeader>
              <CardTitle>Bulk Upload Attendance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">📋 Instructions:</h3>
                <ol className="text-sm space-y-1 ml-4 list-decimal">
                  <li>Select date range for which you want to upload attendance</li>
                  <li>Download template (dates as columns, students as rows)</li>
                  <li>Fill status in each cell: <span className="font-medium">present, absent, leave, half-day, or holiday</span></li>
                  <li>Upload the completed file</li>
                </ol>
              </div>

              {/* Date Range Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    max={today}
                    value={bulkStartDate || ''}
                    onChange={(e) => setBulkStartDate(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    max={today}
                    min={bulkStartDate || ''}
                    value={bulkEndDate || ''}
                    onChange={(e) => setBulkEndDate(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {bulkStartDate && bulkEndDate && (
                <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                  📅 Template will include {Math.ceil((new Date(bulkEndDate) - new Date(bulkStartDate)) / (1000 * 60 * 60 * 24)) + 1} day(s) × {students.length} students = {(Math.ceil((new Date(bulkEndDate) - new Date(bulkStartDate)) / (1000 * 60 * 60 * 24)) + 1) * students.length} rows
                </p>
              )}

              <Button 
                onClick={handleBulkDownload} 
                variant="outline" 
                className="w-full"
                disabled={!bulkStartDate || !bulkEndDate}
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template {bulkStartDate && bulkEndDate ? `(${bulkStartDate} to ${bulkEndDate})` : ''}
              </Button>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setBulkFile(e.target.files[0])}
                  className="hidden"
                  id="bulk-file-input"
                />
                <label htmlFor="bulk-file-input" className="cursor-pointer">
                  <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">
                    {bulkFile ? bulkFile.name : "Click to select file or drag and drop"}
                  </p>
                </label>
              </div>

              {bulkFile && (
                <Button onClick={handleBulkUpload} disabled={loading} className="w-full btn-primary text-white">
                  {loading ? "Uploading..." : "Upload Attendance"}
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AttendanceManagement;
